import React from 'react';
import { Box, Typography } from '@mui/material';
import CapabilityCard from './shared/CapabilityCard';
import GridContainer from './shared/GridContainer';
import HighlightBox from './shared/HighlightBox';
import FeatureList from './shared/FeatureList';

interface DefaultContentProps {
  onContentChange?: (contentId: string) => void;
  onActionClick?: (action: string, data?: any) => void;
}

const DefaultContent: React.FC<DefaultContentProps> = ({ onContentChange, onActionClick }) => {
  return (
    <Box>
      <Typography variant="h4" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--primary-blue)', mb: 2, fontWeight: 700 }}>
        🤖 Welcome to SmartHire AI Assistant
      </Typography>
      <Typography sx={{ fontFamily: 'var(--font-body)', color: 'var(--dark-gray)', mb: 3, fontSize: '1.1rem' }}>
        Your intelligent recruitment partner powered by advanced AI. I'm here to streamline your hiring process from start to finish. Here's what I can do for you:
      </Typography>

      <GridContainer>
        <CapabilityCard
          icon="📋"
          title="Job Description Analysis"
          description="Analyze and summarize job descriptions, extract key requirements, and suggest improvements for better candidate matching."
          contentId="job-description"
          onClick={() => onContentChange?.('job-description')}
        />

        <CapabilityCard
          icon="📊"
          title="Generate Reports"
          description="Create comprehensive reports on candidate analysis, interview summaries, and recruitment metrics."
          contentId="reports"
          onClick={() => onContentChange?.('reports')}
        />

        <CapabilityCard
          icon="🎤"
          title="Voice Chat Support"
          description="Engage in voice conversations for hands-free interaction and real-time assistance during interviews."
          contentId="voice-chat"
          onClick={() => onContentChange?.('voice-chat')}
        />

        <CapabilityCard
          icon="❓"
          title="Interview Questions"
          description="Generate relevant interview questions based on job requirements and candidate profiles."
          contentId="interview-questions"
          onClick={() => onContentChange?.('interview-questions')}
        />

        <CapabilityCard
          icon="🔍"
          title="Resume Screening"
          description="Analyze resumes against job requirements and provide detailed candidate assessments."
          contentId="resume-screening"
          onClick={() => onContentChange?.('resume-screening')}
        />

        <CapabilityCard
          icon="💡"
          title="Smart Recommendations"
          description="Provide intelligent recommendations for hiring decisions based on comprehensive candidate analysis."
          action="show-recommendations"
          onClick={() => onActionClick?.('show-recommendations')}
        />
      </GridContainer>

      <HighlightBox>
        <Typography variant="h6" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--primary-blue)', mb: 2, fontWeight: 600 }}>
          🚀 Quick Start Guide
        </Typography>
        <Typography sx={{ fontFamily: 'var(--font-body)', color: 'var(--dark-gray)', mb: 2 }}>
          Click on any card above or simply ask me questions about:
        </Typography>
        <FeatureList
          items={[
            { text: 'Analyzing and optimizing job descriptions for better candidate attraction', contentId: 'job-description' },
            { text: 'Generating role-specific interview questions with evaluation criteria', contentId: 'interview-questions' },
            { text: 'Creating detailed candidate assessment and recruitment reports', contentId: 'reports' },
            { text: 'AI-powered resume screening with skill matching and ranking', contentId: 'resume-screening' },
            { text: 'Voice-enabled interviews and real-time candidate interactions', contentId: 'voice-chat' },
          ]}
        />
      </HighlightBox>
    </Box>
  );
};

export default DefaultContent;

